/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { FormControl, InputGroup, Button } from 'react-bootstrap';
import classNames from 'classnames';
import useOnClickOutside from '../hooks/useOnClickOutside';
import useSearchDebounce from '../../hooks/useSearchDebounce';
import ProfessionalList from './ProfessionalList';
import SelectedProfessional from './SelectedProfessional';
import { GLOBAL_CALENDAR_SHIFT_TYPES, SEARCH_MIN_CHARS } from '../../constants';

import './ProfessionalSearch.scss';

const mapCaregiverData = (data = []) => {
	return data.map((caregiver) => {
		const { caregiver_name, caregiver_id, caregiver_highest_ranked_licence } = caregiver;
		return {
			...caregiver,
			caregiverName: `${caregiver_name}, ${caregiver_highest_ranked_licence}`,
			resourceType: caregiver_highest_ranked_licence,
			caregiverId: caregiver_id
		};
	});
};

const ProfessionalSearch = ({
	removeSelectedCaregiver = () => {},
	selectedCaregiver,
	setSelectedCaregiver = () => {},
	isOpen,
	setIsOpen = () => {},
	placeholder = 'Filter by Professional',
	emptyListPlaceholder = 'No Shifts for this Professional',
	className,
	searchURL,
	type
}) => {
	const containerRef = useRef(null);
	const [searchValue, setSearchValue] = useState('');
	const [filterCaregivers, setFilterCaregivers] = useState([]);

	const handleResults = (results = []) => {
		setFilterCaregivers(mapCaregiverData(results));
	};

	const { searchTermChange } = useSearchDebounce(
		searchURL,
		searchValue,
		null,
		setSearchValue,
		() => {},
		() => {},
		handleResults,
		SEARCH_MIN_CHARS
	);

	const handleClearBtn = () => {
		setSearchValue('');
		setFilterCaregivers([]);
		if (type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND || type === GLOBAL_CALENDAR_SHIFT_TYPES.ALL) {
			setSelectedCaregiver('');
		}
	};

	const addSelectedCaregiver = (caregiver) => {
		setSelectedCaregiver(caregiver);
		setSearchValue('');
		setFilterCaregivers([]);
		setIsOpen(false);
	};

	const handleSearchTerm = (e) => {
		if (type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND || type === GLOBAL_CALENDAR_SHIFT_TYPES.ALL) {
			setSelectedCaregiver(e.target.value);
		} else {
			searchTermChange(e);
		}
	};

	useOnClickOutside(containerRef, () => setIsOpen(false), 'mousedown');

	useEffect(() => {
		if (setIsOpen && selectedCaregiver === null) {
			setIsOpen(false);
		}
	}, [selectedCaregiver, setIsOpen]);

	useEffect(() => {
		if (!searchValue && filterCaregivers.length > 0) {
			setFilterCaregivers([]);
		}
	}, [searchValue, filterCaregivers]);

	return (
		<>
			<section
				className={classNames('search-professional-container', { active: isOpen }, className)}
				ref={containerRef}
			>
				<InputGroup className="search-professional" onClick={() => !isOpen && setIsOpen(!isOpen)}>
					<InputGroup.Text className="search-icon" />
					{selectedCaregiver && selectedCaregiver.caregiverId?.length > 0 ? (
						<SelectedProfessional caregiver={selectedCaregiver} onClear={removeSelectedCaregiver} />
					) : (
						<>
							<FormControl
								placeholder={placeholder}
								aria-label={placeholder}
								aria-describedby="clear-button"
								value={
									type === GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND || type === GLOBAL_CALENDAR_SHIFT_TYPES.ALL
										? selectedCaregiver || ''
										: searchValue
								}
								onChange={handleSearchTerm}
							/>
							{(searchValue.length > 0 ||
								((GLOBAL_CALENDAR_SHIFT_TYPES.ON_DEMAND || type === GLOBAL_CALENDAR_SHIFT_TYPES.ALL) &&
									selectedCaregiver?.length > 0)) && (
								<Button className="clear-button" id="clear-button" onClick={handleClearBtn}>
									Clear
								</Button>
							)}
						</>
					)}
				</InputGroup>
				{searchValue && searchValue.trim().length >= SEARCH_MIN_CHARS && (
					<ProfessionalList
						searchValue={searchValue}
						caregivers={filterCaregivers}
						isOpen={isOpen}
						onSelect={addSelectedCaregiver}
						placeholder={emptyListPlaceholder}
						searchMinChars={SEARCH_MIN_CHARS}
					/>
				)}
			</section>
		</>
	);
};

export default ProfessionalSearch;
